import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Skeleton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Callout, CardPreOferta, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useLazyGetLeasingListQuery } from "@api/leasing";
import { ApiError } from "@interfaces/response";
import { LeasingMedicalEquipmentDetails, LeasingStructure, LeasingVehicleDetailsPost } from "@interfaces/LeasingTypes";
import { formatCurrencyWhole } from "@helpers/funciones";
import { STATUS_CATALOG } from "@helpers/constantes";
import { AUTO_WUWEI, SEMANAS_POR_MES } from "@pages/producto/credito_simple/wuwei/cotizacionWuWei";
import useStatusConfig from "@hooks/useStatusConfig";

/** `details` del leasing de WuWei: el vehículo (lo guardó "Tus datos") y la pre-oferta (la llena el back, como Mercader). */
type DetallesLeasingWuWei = Partial<LeasingVehicleDetailsPost & LeasingMedicalEquipmentDetails>;

/** Datos que muestra la pre-oferta. Lo que no venga del backend se muestra en 0, como Mercader. */
interface PreOferta {
  /** Marca, modelo y versión, ej. "BYD Dolphin Mini Plus". */
  unidad: string;
  anio: number;
  color: string;
  renta: number;
  plazo: number;
  tasa: number;
  comision: number;
  garantia: number;
  opcionCompra: number;
}

const PRE_OFERTA_VACIA: PreOferta = {
  unidad: `${AUTO_WUWEI.marca} ${AUTO_WUWEI.modelo}`,
  anio: AUTO_WUWEI.anio,
  color: "",
  renta: 0,
  plazo: 0,
  tasa: 0,
  comision: 0,
  garantia: 0,
  opcionCompra: 0,
};

/**
 * Pre-oferta de WuWei (`WW_Oferta` del prototipo): renta semanal, tasa, opción a compra, comisión y renta en garantía,
 * sin tabla de amortización (reglas del prototipo). Mismas fuentes que la de Mercader (`OfertaArrendamiento`):
 * - Condición activa: plazo (`selected_term`) y tasa (`interest_rate`).
 * - `details` del leasing: unidad y color (los guardó "Tus datos"), renta (`monthly_rent`) y renta en garantía
 *   (`security_deposit`).
 * - Solicitud: comisión por apertura, `opening_fee` (%) sobre el valor del leasing (`amount_finance`).
 * - Opción a compra: valor de la unidad (`estimated_value` del leasing o `loan_amount` de la condición) por el
 *   porcentaje residual (el del leasing: aprobado, seleccionado o solicitado; si no, el seleccionado de la condición).
 *   Si el back la manda calculada, se toma de ahí.
 */
export const OfertaWuWei = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.oferta.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const [preOferta, setPreOferta] = useState<PreOferta>(PRE_OFERTA_VACIA);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchPreOferta = async () => {
      try {
        setIsLoading(true);
        const loanApplicationId = String(application.id);
        const [conditions, leasings, loanApplication] = await Promise.all([
          getConditions(loanApplicationId).unwrap(),
          getLeasingList(loanApplicationId).unwrap() as Promise<LeasingStructure<DetallesLeasingWuWei>[]>,
          getLoanApplication(loanApplicationId).unwrap(),
        ]);
        const activeCondition = conditions.find((item) => item.active);
        const leasing = leasings[0];
        const details = leasing?.details;
        const valorUnidad = leasing?.estimated_value || activeCondition?.loan_amount || 0;
        const residualPct =
          leasing?.approved_residual_percentage ||
          leasing?.selected_residual_percentage ||
          leasing?.requested_residual_percentage ||
          activeCondition?.selected_residual_percentage ||
          0;

        setPreOferta({
          unidad:
            details?.make && details?.model
              ? [details.make, details.model, details.version].filter(Boolean).join(" ")
              : PRE_OFERTA_VACIA.unidad,
          anio: details?.year ?? PRE_OFERTA_VACIA.anio,
          color: details?.color ?? "",
          renta: details?.monthly_rent ?? 0,
          plazo: activeCondition?.selected_term ?? 0,
          tasa: activeCondition?.interest_rate ?? 0,
          comision: ((details?.amount_finance ?? 0) * (loanApplication.opening_fee ?? 0)) / 100,
          garantia: details?.security_deposit ?? 0,
          opcionCompra: Math.round((valorUnidad * residualPct) / 100),
        });
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "OfertaWuWei-preoferta" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreOferta();
  }, [application.id, getConditions, getLeasingList, getLoanApplication, dispatch]);

  const { unidad, anio, color, renta, plazo, tasa, comision, garantia, opcionCompra } = preOferta;

  const handleContinuar = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        console.log("aa");
        throw new Error("No se encontró el siguiente status");
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaWuWei-continuar" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Resultado · Pre-aprobado"
        title="¡Tu auto está pre-aprobado!"
        subtitle={
          <>
            Aprobamos el arrendamiento de tu{" "}
            <strong>
              {unidad} {anio}
            </strong>
            {color ? ` en ${color}` : ""}. Revisa tus condiciones y continúa con tu expediente.
          </>
        }
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
      ) : (
        <CardPreOferta
          eyebrow={`Tu renta · ${unidad} a ${plazo} meses (${Math.round(plazo * SEMANAS_POR_MES)} semanas)`}
          badge="Pre-aprobado"
          amount={formatCurrencyWhole(renta)}
          amountCaption="MXN / semana · IVA incluido"
          sub="Descuento automático de tus ganancias · Vigente 30 días · Incluye seguro, mantenimiento y GPS · Sin enganche"
          items={[
            { label: "Tasa anual (s. IVA)", value: `${tasa}%` },
            { label: "Opción a compra", value: formatCurrencyWhole(opcionCompra) },
            { label: "Comisión por apertura", value: formatCurrencyWhole(comision) },
            { label: "Renta en garantía", value: formatCurrencyWhole(garantia) },
          ]}
        />
      )}

      <Box sx={{ mt: "14px" }}>
        <Callout tone="info" compact icon={<InfoOutlined sx={{ fontSize: 14 }} />}>
          Al continuar, abrirás tu <strong>expediente digital</strong> para validar tu identidad y subir tu licencia de
          conducir.
        </Callout>
      </Box>

      <StepActions
        onNext={handleContinuar}
        nextLabel="Continuar al expediente"
        nextDisabled={isLoading || isSubmitting}
        nextTestId="oferta-wuwei-continuar-button"
      />
    </Box>
  );
};
